import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Save, ExternalLink, Trash2, Building2, CreditCard, Link2 } from "lucide-react";

const BLANK = {
  name: "", slug: "", tagline: "", phone: "", email: "", address: "", legal_name: "", accept_user_agreement: false,
  website_url: "", service_area: "", about: "", primary_color: "#0f766e",
  cta_text: "Request Service", active: true, services: [], logo_url: "",
};

function AgencyPaymentsAdmin({ a, onSaved }) {
  const c = a.connect || {};
  const fc = c.fee_config || {};
  const [mode, setMode] = useState(fc.mode === "monthly" ? "monthly" : "percent");
  const [pct, setPct] = useState(String(fc.percent ?? 15));
  const [monthly, setMonthly] = useState(String(fc.monthly_amount ?? 0));
  const [sample, setSample] = useState("120");
  const [domain, setDomain] = useState(a.custom_domain || "");
  const [refundUrl, setRefundUrl] = useState(a.refund_policy_url || "");
  const [busy, setBusy] = useState(false);
  const saveDomain = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/agencies/${a.id}/domain`, { custom_domain: domain });
      setDomain(data.custom_domain || "");
      toast.success(data.custom_domain ? "Custom domain saved" : "Custom domain cleared");
      onSaved && onSaved();
    } catch (e) { toast.error(extractErrorMessage(e, "Could not save domain")); }
    finally { setBusy(false); }
  };
  const saveRefundPolicy = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/agencies/${a.id}/refund-policy`, { refund_policy_url: refundUrl });
      setRefundUrl(data.refund_policy_url || "");
      toast.success(data.refund_policy_url ? "Refund policy saved" : "Refund policy cleared");
      onSaved && onSaved();
    } catch (e) { toast.error(extractErrorMessage(e, "Could not save refund policy")); }
    finally { setBusy(false); }
  };
  const kyc = c.kyc_status || "not_started";
  const tone = {
    active: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending: "text-amber-700 bg-amber-50 border-amber-200",
    restricted: "text-red-700 bg-red-50 border-red-200",
    not_started: "text-slate-600 bg-slate-50 border-slate-200",
  }[kyc] || "text-slate-600 bg-slate-50 border-slate-200";
  const saveFee = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/agencies/${a.id}/fee`, {
        mode, percent: Number(pct) || 0, monthly_amount: Number(monthly) || 0,
      });
      toast.success(mode === "monthly" ? "Fee updated — flat monthly" : "Fee updated");
      onSaved && onSaved();
    } catch { toast.error("Could not update fee"); }
    finally { setBusy(false); }
  };
  const liveConfirm = async (confirmed) => {
    setBusy(true);
    try { await api.put(`/admin/agencies/${a.id}/connect-live-confirm`, { confirmed }); toast.success(confirmed ? "Live payments confirmed" : "Live payments paused"); onSaved && onSaved(); }
    catch (e) { toast.error(extractErrorMessage(e, "Could not update live status")); }
    finally { setBusy(false); }
  };
  const [chk, setChk] = useState(null);
  const runCheck = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/agencies/${a.id}/go-live-check`); setChk(data); }
    catch { toast.error("Could not run readiness check"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid={`agency-payments-admin-${a.slug}`}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><CreditCard className="h-3.5 w-3.5" /> Payments</span>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${tone}`} data-testid={`agency-kyc-${a.slug}`}>{kyc.replace("_", " ")}</span>
      </div>
      <div className="mt-2 rounded-md border border-slate-200 p-2" data-testid={`agency-domain-row-${a.slug}`}>
        <span className="text-xs font-medium text-slate-500">Custom domain (white-label)</span>
        <div className="mt-1 flex items-center gap-2">
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="jeffsprocessservers.com" className="h-8" data-testid={`agency-domain-input-${a.slug}`} />
          <Button size="sm" variant="outline" onClick={saveDomain} disabled={busy} data-testid={`agency-domain-save-${a.slug}`}>Save</Button>
        </div>
        <p className="mt-1 text-[11px] text-slate-400">CNAME this to the ARPS host (Manage Publishes → Domain) for SSL. The slug site /a/{a.slug} works today.</p>
      </div>
      <div className="mt-2 rounded-md border border-slate-200 p-2" data-testid={`agency-refund-policy-row-${a.slug}`}>
        <span className="text-xs font-medium text-slate-500">Refund &amp; Dispute Policy {a.refund_policy_version ? <span className="text-emerald-600">· {a.refund_policy_version}</span> : null}</span>
        <div className="mt-1 flex items-center gap-2">
          <Input value={refundUrl} onChange={(e) => setRefundUrl(e.target.value)} placeholder="https://agency.com/refund-policy" className="h-8" data-testid={`agency-refund-policy-input-${a.slug}`} />
          <Button size="sm" variant="outline" onClick={saveRefundPolicy} disabled={busy} data-testid={`agency-refund-policy-save-${a.slug}`}>Save</Button>
        </div>
        <p className="mt-1 text-[11px] text-slate-400">Link to this agency's own refund/dispute policy. Shown on their branded checkout instead of ARPS's. Leave blank to show a generic placeholder (no checkbox).</p>
      </div>
      <div className="mt-2 space-y-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-xs font-medium text-slate-500">Fee type</span>
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <input type="radio" name={`feemode-${a.slug}`} checked={mode === "percent"} onChange={() => setMode("percent")} data-testid={`agency-fee-mode-percent-${a.slug}`} /> Revenue-share %
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <input type="radio" name={`feemode-${a.slug}`} checked={mode === "monthly"} onChange={() => setMode("monthly")} data-testid={`agency-fee-mode-monthly-${a.slug}`} /> Flat monthly fee
          </label>
        </div>
        <div className="flex items-center gap-2">
          {mode === "percent" ? (
            <>
              <Input type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} className="h-8 w-20" data-testid={`agency-fee-input-${a.slug}`} />
              <span className="text-xs text-slate-500">% platform fee</span>
            </>
          ) : (
            <>
              <span className="text-xs text-slate-500">$</span>
              <Input type="number" min="0" step="0.01" value={monthly} onChange={(e) => setMonthly(e.target.value)} className="h-8 w-28" data-testid={`agency-fee-monthly-${a.slug}`} />
              <span className="text-xs text-slate-500">/ month</span>
            </>
          )}
          <Button size="sm" variant="outline" onClick={saveFee} disabled={busy} data-testid={`agency-fee-save-${a.slug}`}>Save</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-sky-50 px-2 py-1.5 ring-1 ring-sky-100" data-testid={`agency-fee-effective-${a.slug}`}>
          <span className="text-xs font-medium text-slate-500">Effective fee</span>
          {mode === "percent" && (
            <span className="flex items-center gap-1 text-xs text-slate-500">on a $<Input type="number" min="0" step="1" value={sample} onChange={(e) => setSample(e.target.value)} className="h-6 w-16" data-testid={`agency-fee-sample-${a.slug}`} /> order</span>
          )}
          <span className="rounded bg-white px-2 py-0.5 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200" data-testid={`agency-fee-effective-result-${a.slug}`}>
            {mode === "monthly"
              ? `Flat $${(Number(monthly) || 0).toFixed(2)} / month (no per-payment fee)`
              : `${(Number(pct) || 0)}% → $${((Number(pct) || 0) / 100 * (Number(sample) || 0)).toFixed(2)}`}
          </span>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
        <span className="text-xs text-slate-500">Live payments {c.live_confirmed ? "confirmed" : "not confirmed"}</span>
        {c.live_confirmed
          ? <Button size="sm" variant="ghost" className="text-xs text-red-600" onClick={() => liveConfirm(false)} disabled={busy} data-testid={`agency-live-pause-${a.slug}`}>Pause</Button>
          : <Button size="sm" variant="outline" className="text-xs" onClick={() => liveConfirm(true)} disabled={busy || !c.charges_enabled} data-testid={`agency-live-confirm-${a.slug}`}>Confirm live payments</Button>}
      </div>
      <div className="mt-2 border-t border-slate-100 pt-2">
        <Button size="sm" variant="outline" className="text-xs" onClick={runCheck} disabled={busy} data-testid={`agency-golive-check-${a.slug}`}>Run go-live readiness check</Button>
        {chk && (
          <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2" data-testid={`agency-golive-panel-${a.slug}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600">{chk.mode === "live" ? "LIVE" : "TEST"} · {chk.fee_model?.label}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${chk.ready_for_live_money ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`agency-golive-ready-${a.slug}`}>
                {chk.ready_for_live_money ? "READY" : "BLOCKED"}
              </span>
            </div>
            <ul className="mt-1.5 space-y-1">
              {chk.gates.map((g) => (
                <li key={g.key} className="flex items-center gap-1.5 text-xs" data-testid={`golive-gate-${g.key}`}>
                  <span className={g.ok ? "text-emerald-600" : "text-red-500"}>{g.ok ? "●" : "○"}</span>
                  <span className={g.ok ? "text-slate-600" : "text-slate-800 font-medium"}>{g.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminAgencies() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view; object = form
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/agencies"); setList(data); }
    catch { toast.error("Could not load agencies"); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...BLANK });
  const startEdit = (a) => setEditing({ ...BLANK, ...a, services: a.services || [] });

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/a/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Branded link copied");
    } catch {
      toast.error(url);
    }
  };

  const set = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.value }));
  const setService = (i, k) => (e) =>
    setEditing((s) => ({ ...s, services: s.services.map((x, j) => j === i ? { ...x, [k]: e.target.value } : x) }));
  const addService = () => setEditing((s) => ({ ...s, services: [...s.services, { title: "", description: "" }] }));
  const removeService = (i) => setEditing((s) => ({ ...s, services: s.services.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Agency name is required");
    if (!editing.id) {
      if (!(editing.legal_name || "").trim()) return toast.error("Agency legal name is required");
      if (!editing.accept_user_agreement) return toast.error("Accept the ARPS Platform Subscriber Agreement to create the agency");
    }
    setBusy(true);
    try {
      const payload = { ...editing, slug: editing.slug || editing.name, active: editing.active !== false };
      if (editing.id) await api.put(`/admin/agencies/${editing.id}`, payload);
      else await api.post(`/admin/agencies`, payload);
      toast.success("Agency saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div data-testid="agency-form" className="mx-auto max-w-3xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-bold text-slate-900">{editing.id ? "Edit agency" : "New agency"}</h1>
          <Button variant="outline" onClick={() => setEditing(null)} data-testid="agency-cancel">Back</Button>
        </div>
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
          <div><Label>Name *</Label><Input data-testid="agency-name-input" value={editing.name} onChange={set("name")} /></div>
          <div><Label>URL slug</Label><Input data-testid="agency-slug-input" value={editing.slug} onChange={set("slug")} placeholder="auto from name" /></div>
          <div className="sm:col-span-2"><Label>Tagline</Label><Input value={editing.tagline} onChange={set("tagline")} /></div>
          <div><Label>Phone</Label><Input value={editing.phone} onChange={set("phone")} /></div>
          <div><Label>Email</Label><Input value={editing.email} onChange={set("email")} /></div>
          <div className="sm:col-span-2"><Label>Address</Label><Input value={editing.address} onChange={set("address")} /></div>
          <div><Label>Website URL</Label><Input value={editing.website_url} onChange={set("website_url")} /></div>
          <div><Label>Service area</Label><Input value={editing.service_area} onChange={set("service_area")} /></div>
          <div><Label>Logo URL</Label><Input value={editing.logo_url} onChange={set("logo_url")} placeholder="optional" /></div>
          <div><Label>Brand color</Label><Input type="color" value={editing.primary_color} onChange={set("primary_color")} className="h-10 w-20 p-1" /></div>
          <div><Label>CTA text</Label><Input value={editing.cta_text} onChange={set("cta_text")} /></div>
          <div className="sm:col-span-2"><Label>About</Label><Textarea rows={4} value={editing.about} onChange={set("about")} /></div>
          {!editing.id && (
            <div className="sm:col-span-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div><Label>Legal Name *</Label><Input data-testid="agency-legal-name-input" value={editing.legal_name} onChange={set("legal_name")} placeholder="Agency business legal name" /></div>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input data-testid="agency-accept-user-agreement" type="checkbox" checked={editing.accept_user_agreement} onChange={(e) => setEditing((s) => ({ ...s, accept_user_agreement: e.target.checked }))} className="mt-1" />
                <span>The agency has read and accepts the <a data-testid="agency-view-user-agreement" href={`${api.defaults.baseURL}/user-agreement/preview?legal_name=${encodeURIComponent(editing.legal_name || "")}`} target="_blank" rel="noreferrer" className="font-semibold text-emerald-600 underline">ARPS Platform Subscriber Agreement</a>.</span>
              </label>
            </div>
          )}
          <div className="sm:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <Label>Services</Label>
              <Button size="sm" variant="outline" onClick={addService} data-testid="agency-add-service"><Plus className="mr-1 h-4 w-4" />Add</Button>
            </div>
            <div className="space-y-2">
              {editing.services.map((s, i) => (
                <div key={i} className="flex gap-2" data-testid={`agency-service-row-${i}`}>
                  <Input className="w-48" placeholder="Title" value={s.title} onChange={setService(i, "title")} />
                  <Input className="flex-1" placeholder="Description" value={s.description} onChange={setService(i, "description")} />
                  <button onClick={() => removeService(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button data-testid="agency-save" onClick={save} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800"><Save className="mr-2 h-4 w-4" />Save agency</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-agencies" className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-slate-900">Agencies</h1>
        <Button data-testid="agency-new" onClick={startNew} className="bg-slate-900 text-white hover:bg-slate-800"><Plus className="mr-2 h-4 w-4" />New agency</Button>
      </div>
      <p className="text-sm text-slate-500">Reusable branded process-serving websites + client intake. Each agency has a public page at <code>/a/&lt;slug&gt;</code>.</p>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="agency-docs-note">
        <p className="font-semibold text-slate-700">Operations docs</p>
        <p className="mt-1 text-xs text-slate-500">Reference guides live in the repo under <code>memory/</code>:</p>
        <ul className="mt-1 space-y-0.5 text-xs">
          <li data-testid="agency-doc-runbook"><code>memory/LIVE_VALIDATION_RUNBOOK.md</code> — one-time live-money proof steps</li>
          <li data-testid="agency-doc-manual"><code>memory/AGENCY_OPERATIONS_MANUAL.md</code> — day-to-day agency operations</li>
        </ul>
      </div>
      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">No agencies yet.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((a) => (
            <div key={a.id} data-testid={`agency-card-${a.slug}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg text-white" style={{ backgroundColor: a.primary_color || "#0f766e" }}><Building2 className="h-5 w-5" /></span>
                <div>
                  <p className="font-semibold text-slate-900">{a.name}</p>
                  <p className="text-xs text-slate-500">/a/{a.slug}</p>
                </div>
              </div>
              {a.tagline && <p className="mt-3 text-sm text-slate-600">{a.tagline}</p>}
              <AgencyPaymentsAdmin a={a} onSaved={load} />
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(a)} data-testid={`agency-edit-${a.slug}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => copyLink(a.slug)} data-testid={`agency-copy-link-${a.slug}`}>
                  <Link2 className="mr-1 h-3.5 w-3.5" /> Copy link
                </Button>
                <a href={`/a/${a.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid={`agency-view-${a.slug}`}>
                  View <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
